import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, DatePicker, Dropdown, Empty, Input, Menu, Segmented, Select, Table, Tag, Tooltip, message } from "antd";
import {
  DownOutlined,
  FileExcelOutlined,
  PrinterOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import * as dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/nl_NL";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import { ListCard, Num, PageHeader, RecordLink, StatCards } from "components/shared-components/purchase";
import Barcode from "components/shared-components/Table/Barcode";
import { exportToExcel } from "components/shared-components/ExcelExport";
import { CURRENCY } from "constants/DeliveryConstants";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import { COMPANY_CONSTANTS } from "constants/CompanyConstants";
import { formatPrice } from "functions/formatters";
import { getBarcodeDataExact } from "functions/orders";
import { getExactOrdersExcelExportData } from "functions/excel";
import { getExactSalesOrders } from "functions/api/exact";
import {
  DELIVERY_FILTER,
  EXACT_DELIVERY_STATUS_META,
  EXACT_ORDER_STATUS,
  EXACT_ORDER_STATUS_META,
  filterExactOrders,
  getCustomerOptions,
  getExactOrderTotals,
  getSalespersonOptions,
  getSelectedOrAll,
  groupExactOrdersByCustomer,
  summariseExactOrders,
} from "functions/exactOrders";
import { saveExactOrders } from "../../../../redux/actions/Exact";
import urls from "urls";
import { useWebshopUsers } from "../../../../data/hooks/useUsers";

const { RangePicker } = DatePicker;

/**
 * Bestellingen → Exact: de verkooporders zoals ze in Exact Online staan.
 *
 * Deze pagina bestond al, maar toonde vier kolommen en kon alleen op referentie
 * zoeken. Sinds BMX-54 is het scherm bedoeld als vervanging van Exact Online
 * zelf voor wie alleen hoeft te kíjken — vandaar het zoeken over regels, de
 * filters, de detailpagina en het tabblad "Per klant".
 *
 * Twee dingen die de indeling verklaren:
 *
 * 1. **Statussen en periode worden op de server gefilterd**, de rest hier. Exact
 *    levert alleen wat je in het `$filter` vraagt, dus die twee selects halen
 *    opnieuw op; zoeken, klant, levering en verkoper werken op wat er al staat.
 *    Standaard is "lopend": open (12) + deels geleverd (20). Die laatste is de
 *    backorder, en die ontbrak hiervoor volledig — de API vroeg alleen status 12.
 * 2. **Openstaand komt uit de regels**, niet uit de orderstatus: besteld min
 *    geleverd per regel. Zie functions/exactOrders.js; die logica is los getoetst.
 */

const SCOPE_RUNNING = [EXACT_ORDER_STATUS.OPEN, EXACT_ORDER_STATUS.PARTIAL];
const PERIOD_OPTIONS = [3, 6, 12, 24];
const DEFAULT_PERIOD = 12;

const parseStatuses = (value) => {
  if (!value) return SCOPE_RUNNING;
  const parsed = value
    .split(",")
    .map((part) => Number(part))
    .filter((code) => Object.values(EXACT_ORDER_STATUS).includes(code));
  return parsed.length ? parsed : SCOPE_RUNNING;
};

const ExactOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const barcodeRef = useRef();
  const [messageApi, contextHolder] = message.useMessage();
  const [searchParams, setSearchParams] = useSearchParams();

  const exact = useSelector((state) => state.exact);
  const products = useSelector((state) => state.firestoreReducer.ordered.products);
  const customers = useWebshopUsers();

  // Server-side (halen opnieuw op als ze wijzigen).
  const [statuses, setStatuses] = useState(() => parseStatuses(searchParams.get("status")));
  const [months, setMonths] = useState(() => {
    const value = searchParams.get("months");
    return value === null ? DEFAULT_PERIOD : Number(value);
  });

  // Client-side.
  const [searchText, setSearchText] = useState(searchParams.get("q") || "");
  const [customerFilter, setCustomerFilter] = useState(searchParams.get("customer") || null);
  const [deliveryFilter, setDeliveryFilter] = useState(searchParams.get("delivery") || DELIVERY_FILTER.ALL);
  const [salespersonFilter, setSalespersonFilter] = useState(searchParams.get("salesperson") || null);
  const [dateRange, setDateRange] = useState(null);
  const [tab, setTab] = useState(searchParams.get("tab") === "customers" ? "customers" : "orders");

  const [isLoading, setIsLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const orders = exact?.orders;
  const fetchedAt = exact?.fetchedAt;
  const loadedQuery = exact?.query;
  const currentQuery = useMemo(() => ({ status: [...statuses].sort().join(","), months }), [statuses, months]);

  const updateSearchParams = useCallback(
    (changes) => {
      setSearchParams(
        (params) => {
          Object.entries(changes).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") params.delete(key);
            else params.set(key, String(value));
          });
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const fetchOrders = useCallback(
    async (query) => {
      setIsLoading(true);
      try {
        const data = await getExactSalesOrders({ status: query.status, months: query.months });
        dispatch(saveExactOrders(data, query));
        setSelectedRowKeys([]);
      } catch (error) {
        console.error("Ophalen Exact-bestellingen mislukt:", error);
        messageApi.error(t("notifications.error_retrieving_exact_orders"));
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, messageApi, t],
  );

  // Automatisch ophalen zodra de store leeg is of iets anders bevat dan de
  // gekozen statussen/periode. Voorheen moest je altijd eerst op "Synchroniseren"
  // drukken en stond je na elke refresh naar een leeg scherm te kijken — de
  // lijst leeft alleen in redux-geheugen en overleeft een herlaadbeurt niet.
  useEffect(() => {
    const sameQuery =
      loadedQuery && loadedQuery.status === currentQuery.status && loadedQuery.months === currentQuery.months;
    if (isLoading || sameQuery) return;
    fetchOrders(currentQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuery]);

  const summaries = useMemo(() => summariseExactOrders(orders, customers), [orders, customers]);

  const filtered = useMemo(
    () =>
      filterExactOrders(summaries, {
        search: searchText,
        customer: customerFilter,
        delivery: deliveryFilter,
        salesperson: salespersonFilter,
        dateRange: dateRange ? [dateRange[0]?.startOf("day").valueOf(), dateRange[1]?.endOf("day").valueOf()] : null,
      }),
    [summaries, searchText, customerFilter, deliveryFilter, salespersonFilter, dateRange],
  );

  const totals = useMemo(() => getExactOrderTotals(filtered), [filtered]);
  // Printen en exporteren werken op hetzelfde: de aangevinkte rijen, of anders
  // alles wat er staat. Zie getSelectedOrAll — die twee liepen uiteen.
  const actionRows = useMemo(() => getSelectedOrAll(filtered, selectedRowKeys), [filtered, selectedRowKeys]);
  const selectionCount = useMemo(
    () => filtered.filter((summary) => selectedRowKeys.includes(summary.id)).length,
    [filtered, selectedRowKeys],
  );
  const byCustomer = useMemo(() => groupExactOrdersByCustomer(filtered), [filtered]);
  const customerOptions = useMemo(() => getCustomerOptions(summaries), [summaries]);
  const salespersonOptions = useMemo(() => getSalespersonOptions(summaries), [summaries]);

  const hasFilters =
    !!searchText || !!customerFilter || !!salespersonFilter || deliveryFilter !== DELIVERY_FILTER.ALL || !!dateRange;

  const clearFilters = () => {
    setSearchText("");
    setCustomerFilter(null);
    setSalespersonFilter(null);
    setDeliveryFilter(DELIVERY_FILTER.ALL);
    setDateRange(null);
    updateSearchParams({ q: null, customer: null, salesperson: null, delivery: null });
  };

  const handleBarcodesPrint = useReactToPrint({
    contentRef: barcodeRef,
    onAfterPrint: () => setIsPrinting(false),
  });

  const handleManualPrint = () => {
    if (!selectionCount) {
      messageApi.info(t("exact.select_orders_first"));
      return;
    }
    setIsPrinting(true);
    setTimeout(() => handleBarcodesPrint(), 1000);
  };

  const handleExcelExport = () => {
    exportToExcel(
      getExactOrdersExcelExportData(actionRows),
      `${COMPANY_CONSTANTS.WEBSITE_NAME} Exact ${dayjs().format(DATE_FORMAT_DD_MM_YYYY)}`,
    );
  };

  // Levering en facturatie zijn twee aparte velden in Exact; `Status` is de
  // samenvatting ervan. Die samenvatting stond hier eerst als enige kolom, met
  // als gevolg "Deels geleverd" naast een Open van nul — dat is een order die
  // volledig geleverd maar nog niet gefactureerd is. Nu allebei apart.
  const statusTag = (status, meta, group) => {
    const entry = meta[status];
    if (!entry) return <Tag>{t("exact.status.unknown")}</Tag>;
    return <Tag color={entry.color}>{t(`exact.${group}.${entry.key}`)}</Tag>;
  };

  const formatDate = (ms) => (ms === null ? "-" : dayjs(ms).format(DATE_FORMAT_DD_MM_YYYY));

  const orderColumns = [
    {
      title: t("exact.order_number"),
      dataIndex: "orderNumber",
      width: 110,
      render: (value, row) => (
        <RecordLink to={urls.exactOrderDetail + row.id}>{value || t("exact.no_order_number")}</RecordLink>
      ),
      sorter: (a, b) => (a.orderNumber || 0) - (b.orderNumber || 0),
    },
    {
      title: t("exact.reference"),
      dataIndex: "yourRef",
      width: 140,
      render: (value) => <Num muted={!value}>{value || "-"}</Num>,
      sorter: (a, b) => (a.yourRef || "").localeCompare(b.yourRef || ""),
    },
    {
      title: t("customer.key"),
      dataIndex: "customerName",
      render: (value) => value || "-",
      sorter: (a, b) => (a.customerName || "").localeCompare(b.customerName || "", "nl"),
    },
    {
      title: t("order_date"),
      dataIndex: "orderDate",
      width: 120,
      render: formatDate,
      sorter: (a, b) => (a.orderDate || 0) - (b.orderDate || 0),
      defaultSortOrder: "descend",
    },
    {
      title: t("exact.delivery"),
      dataIndex: "deliveryStatus",
      width: 140,
      render: (value) => statusTag(value, EXACT_DELIVERY_STATUS_META, "delivery_status"),
      sorter: (a, b) => (a.deliveryStatus || 0) - (b.deliveryStatus || 0),
    },
    {
      // Regels en openstaand zaten eerst in twee kolommen. Het aantal regels is
      // wat je scant; hoeveel daarvan al geleverd is, wil je pas weten als je
      // bij die ene rij stilstaat. Vandaar op hover.
      title: t("exact.lines"),
      dataIndex: "lineCount",
      width: 90,
      align: "right",
      render: (value, row) => (
        <Tooltip title={`${row.deliveredQuantity} / ${row.orderedQuantity} ${t("exact.delivered").toLowerCase()}`}>
          <Num align="right" strong={row.openQuantity > 0}>
            {value}
          </Num>
        </Tooltip>
      ),
      sorter: (a, b) => a.lineCount - b.lineCount,
    },
    {
      title: t("total"),
      dataIndex: "amount",
      width: 120,
      align: "right",
      render: (value) => <Num align="right">{CURRENCY + formatPrice(value)}</Num>,
      sorter: (a, b) => a.amount - b.amount,
    },
  ];

  const customerColumns = [
    {
      title: t("customer.key"),
      dataIndex: "customerName",
      render: (value) => value || "-",
      sorter: (a, b) => (a.customerName || "").localeCompare(b.customerName || "", "nl"),
    },
    {
      title: t("order.orders"),
      dataIndex: "orderCount",
      width: 110,
      align: "right",
      render: (value) => <Num align="right">{value}</Num>,
      sorter: (a, b) => a.orderCount - b.orderCount,
    },
    {
      title: t("exact.backorders"),
      dataIndex: "backorderCount",
      width: 120,
      align: "right",
      render: (value) => (
        <Num align="right" strong={value > 0} muted={value === 0}>
          {value}
        </Num>
      ),
      sorter: (a, b) => a.backorderCount - b.backorderCount,
    },
    {
      title: t("exact.oldest_order"),
      dataIndex: "oldestOrderDate",
      width: 140,
      render: formatDate,
      sorter: (a, b) => (a.oldestOrderDate || 0) - (b.oldestOrderDate || 0),
    },
    {
      title: t("exact.open_quantity"),
      dataIndex: "openQuantity",
      width: 120,
      align: "right",
      render: (value) => (
        <Num align="right" strong={value > 0} muted={value === 0}>
          {value}
        </Num>
      ),
      sorter: (a, b) => a.openQuantity - b.openQuantity,
      defaultSortOrder: "descend",
    },
    {
      title: t("total"),
      dataIndex: "amount",
      width: 130,
      align: "right",
      render: (value) => <Num align="right">{CURRENCY + formatPrice(value)}</Num>,
      sorter: (a, b) => a.amount - b.amount,
    },
  ];

  const subtitle = [
    t("exact.order_count", { count: totals.orderCount }),
    t("exact.open_count", { count: totals.openCount }),
    t("exact.backorder_count", { count: totals.backorderCount }),
    fetchedAt ? t("exact.fetched_at", { time: dayjs(fetchedAt).format("HH:mm") }) : t("exact.never_fetched"),
  ].join(" · ");

  const actionsMenu = (
    <Menu
      items={[
        {
          key: "excel",
          icon: <FileExcelOutlined />,
          label: selectionCount ? `${t("excel")} (${selectionCount})` : t("excel"),
          onClick: handleExcelExport,
        },
        {
          key: "barcodes",
          icon: <PrinterOutlined />,
          label: selectionCount ? `${t("order.barcodes")} (${selectionCount})` : t("order.barcodes"),
          onClick: handleManualPrint,
        },
      ]}
    />
  );

  return (
    <>
      {contextHolder}
      <PageHeader
        title={t("order.exact")}
        subtitle={subtitle}
        extra={
          <>
            <Dropdown overlay={actionsMenu}>
              <Button>
                {t("actions")} <DownOutlined />
              </Button>
            </Dropdown>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={isLoading}
              onClick={() => fetchOrders(currentQuery)}
            >
              {t("exact.fetch")}
            </Button>
          </>
        }
      />

      <StatCards
        items={[
          { label: t("exact.outstanding_quantity"), value: totals.outstandingOrderCount },
          { label: t("exact.backorders"), value: totals.backorderCount, tone: totals.backorderCount ? "warning" : undefined },
          { label: t("exact.order_value"), value: CURRENCY + formatPrice(totals.amount) },
          { label: t("exact.customers_with_orders"), value: totals.customerCount },
        ]}
      />

      <ListCard
        filters={
          <>
            <Input
              value={searchText}
              placeholder={t("exact.search_placeholder")}
              prefix={<SearchOutlined />}
              allowClear
              style={{ width: 260 }}
              onChange={(e) => {
                setSearchText(e.target.value);
                updateSearchParams({ q: e.target.value });
              }}
            />
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t("customer.key")}
              value={customerFilter}
              style={{ minWidth: 220 }}
              options={customerOptions}
              onChange={(value) => {
                setCustomerFilter(value ?? null);
                updateSearchParams({ customer: value ?? null });
              }}
            />
            <Select
              value={deliveryFilter}
              style={{ minWidth: 200 }}
              onChange={(value) => {
                setDeliveryFilter(value);
                updateSearchParams({ delivery: value === DELIVERY_FILTER.ALL ? null : value });
              }}
              options={[
                { value: DELIVERY_FILTER.ALL, label: t("exact.delivery_all") },
                { value: DELIVERY_FILTER.OUTSTANDING, label: t("exact.delivery_outstanding") },
                { value: DELIVERY_FILTER.BACKORDER, label: t("exact.delivery_backorder") },
                { value: DELIVERY_FILTER.DELIVERED, label: t("exact.delivery_delivered") },
              ]}
            />
            <Select
              mode="multiple"
              maxTagCount="responsive"
              value={statuses}
              style={{ minWidth: 220 }}
              placeholder={t("exact.overall_status")}
              onChange={(value) => {
                const next = value.length ? value : SCOPE_RUNNING;
                setStatuses(next);
                updateSearchParams({ status: [...next].sort().join(",") });
              }}
              options={Object.entries(EXACT_ORDER_STATUS_META).map(([code, meta]) => ({
                value: Number(code),
                label: t(`exact.status.${meta.key}`),
              }))}
            />
            <Select
              value={months}
              style={{ minWidth: 150 }}
              onChange={(value) => {
                setMonths(value);
                updateSearchParams({ months: value });
              }}
              options={[
                ...PERIOD_OPTIONS.map((value) => ({ value, label: t("exact.period_months", { count: value }) })),
                { value: 0, label: t("exact.period_all") },
              ]}
            />
            <RangePicker
              locale={locale}
              format={DATE_FORMAT_DD_MM_YYYY}
              value={dateRange}
              onChange={(value) => setDateRange(value)}
            />
            {salespersonOptions.length > 1 ? (
              <Select
                allowClear
                placeholder={t("exact.salesperson")}
                value={salespersonFilter}
                style={{ minWidth: 160 }}
                options={salespersonOptions}
                onChange={(value) => {
                  setSalespersonFilter(value ?? null);
                  updateSearchParams({ salesperson: value ?? null });
                }}
              />
            ) : null}
            {hasFilters ? <Button type="link" onClick={clearFilters}>{t("exact.clear_filters")}</Button> : null}
            <Segmented
              style={{ marginLeft: "auto" }}
              value={tab}
              onChange={(value) => {
                setTab(value);
                updateSearchParams({ tab: value === "customers" ? "customers" : null });
              }}
              options={[
                { value: "orders", label: t("exact.tab_orders") },
                { value: "customers", label: t("exact.tab_customers") },
              ]}
            />
          </>
        }
        footnote={t("exact.footnote")}
      >
        <div className="table-responsive">
          {tab === "orders" ? (
            <Table
              columns={orderColumns}
              dataSource={filtered}
              rowKey="id"
              loading={isLoading}
              scroll={{ x: 1100 }}
              locale={{ emptyText: <Empty description={t("exact.no_orders")} /> }}
              rowSelection={{
                selectedRowKeys,
                type: "checkbox",
                onChange: (keys) => setSelectedRowKeys(keys),
              }}
              onRow={(row) => ({
                onClick: () => navigate(urls.exactOrderDetail + row.id),
                style: { cursor: "pointer" },
              })}
            />
          ) : (
            <Table
              columns={customerColumns}
              dataSource={byCustomer}
              rowKey="key"
              loading={isLoading}
              scroll={{ x: 900 }}
              locale={{ emptyText: <Empty description={t("exact.no_orders")} /> }}
              onRow={(row) => ({
                // Doorklikken vanuit "Per klant" zet het klantfilter en springt
                // terug naar de orderlijst — dat is wat je wilt na "staat er nog
                // een backorder voor deze klant".
                onClick: () => {
                  setCustomerFilter(row.customerGuid);
                  setTab("orders");
                  updateSearchParams({ customer: row.customerGuid, tab: null });
                },
                style: { cursor: "pointer" },
              })}
            />
          )}
        </div>
      </ListCard>

      <div style={{ display: "none" }}>
        {isPrinting ? (
          <Barcode ref={barcodeRef} data={getBarcodeDataExact(actionRows.map((row) => row.raw), products)} />
        ) : null}
      </div>
    </>
  );
};

export default ExactOrders;
